import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useLaunchMode } from '../hooks/useLaunchMode';
import { LaunchPricingNotice } from '../components/LaunchPricingNotice';
import { validatePIVA } from '../components/CrafterBadge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Check, 
  X, 
  Sparkles, 
  Crown,
  Heart,
  Briefcase,
  ArrowRight,
  AlertCircle,
  Zap,
  TrendingUp,
  Clock,
  BarChart3,
  Eye,
  MessageSquare,
  Globe
} from 'lucide-react';

export default function PlansPage() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [showActivationDialog, setShowActivationDialog] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [vatNumber, setVatNumber] = useState('');
  const [vatError, setVatError] = useState('');
  const [activating, setActivating] = useState(false);
  
  // Check if user is already a crafter
  const isCrafterEnabled = profile?.role === 'crafter' || profile?.is_crafter_enabled;
  const currentTier = profile?.tier || 'free';
  const launchMode = useLaunchMode();

  const handleSelectPlan = (planId, crafterType) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    if (isCrafterEnabled && planId === 'free') {
      toast.info('Sei già un artigiano! Vai alla dashboard per gestire il tuo profilo.');
      navigate('/dashboard');
      return;
    }

    if (planId === 'premium' && launchMode) {
      toast.info('Durante il lancio tutti i vantaggi Premium sono inclusi gratuitamente!');
      return;
    }

    if (planId === 'premium' && isCrafterEnabled) {
      // TODO: Implement Stripe subscription
      toast.info('L\'abbonamento Premium sarà disponibile a breve!');
      return;
    }
    
    // Open activation dialog with selected type
    setSelectedType(crafterType);
    setShowActivationDialog(true);
  };

  const handleActivateCrafter = async () => {
    let cleanedPiva = null;
    if (selectedType === 'professionista') {
      const validation = validatePIVA(vatNumber);
      if (!validation.valid) {
        toast.error(validation.error || 'Partita IVA non valida');
        return;
      }
      cleanedPiva = validation.cleaned;
    }

    setActivating(true);
    try {
      const updateData = {
        role: 'crafter',
        is_crafter_enabled: true,
        crafter_type: selectedType,
        tier: 'free'
      };

      if (selectedType === 'professionista') {
        updateData.partita_iva = cleanedPiva;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) throw error;

      toast.success(
        selectedType === 'hobbista' 
          ? 'Benvenuto come Hobbista! 🎨 Configura la tua vetrina.' 
          : 'Benvenuto come Artigiano Professionista! 🏆'
      );
      
      if (refreshProfile) {
        await refreshProfile();
      }
      
      setShowActivationDialog(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error activating crafter:', error);
      toast.error('Errore durante l\'attivazione');
    } finally {
      setActivating(false);
    }
  };

  const crafterTypes = [
    {
      id: 'hobbista',
      name: 'Hobbista',
      icon: Heart,
      color: 'emerald',
      badge: '🟢 Hobbista Italia',
      description: 'Crea per passione, vendi le tue creazioni senza bisogno di P.IVA',
      requirements: [
        'Nessuna P.IVA richiesta',
        'Ideale per chi inizia',
        'Perfetto per creazioni occasionali'
      ]
    },
    {
      id: 'professionista',
      name: 'Artigiano Professionista',
      icon: Briefcase,
      color: 'red',
      badge: '🔴 Artigiano P.IVA',
      description: 'Professionista con partita IVA, massima credibilità e fiducia',
      requirements: [
        'P.IVA obbligatoria',
        'Massima visibilità',
        'Badge di verifica esclusivo'
      ]
    }
  ];

  // New simplified plans
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '€0',
      period: 'per sempre',
      description: 'Tutto il necessario per iniziare',
      features: [
        { text: 'Vetrina completa', included: true },
        { text: 'Prodotti illimitati', included: true },
        { text: 'Richieste personalizzate', included: true },
        { text: '5 risposte a richieste/settimana', included: true },
        { text: 'Messaggistica', included: true },
        { text: 'Analytics base', included: true },
        { text: 'Link Social e Sito Web', included: false },
        { text: 'Badge Creator Plus', included: false },
        { text: 'Priorità nuove richieste', included: false },
        { text: 'Boost visibilità', included: false },
        { text: 'Analytics avanzate', included: false },
      ],
      cta: isCrafterEnabled ? 'Piano attuale' : 'Inizia Gratis',
      highlighted: false,
      disabled: isCrafterEnabled && currentTier === 'free'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '€10',
      period: '/mese',
      description: 'Massimizza la tua visibilità',
      features: [
        { text: 'Vetrina completa', included: true },
        { text: 'Prodotti illimitati', included: true },
        { text: 'Richieste personalizzate', included: true },
        { text: 'Risposte illimitate', included: true, highlight: true },
        { text: 'Messaggistica', included: true },
        { text: 'Link Social e Sito Web', included: true, highlight: true },
        { text: 'Analytics avanzate', included: true, highlight: true },
        { text: 'Badge Creator Plus', included: true, highlight: true },
        { text: 'Priorità nuove richieste', included: true, highlight: true },
        { text: 'Boost visibilità', included: true, highlight: true },
        { text: 'Ranking ricerca migliorato', included: true, highlight: true },
      ],
      cta: currentTier === 'pro' ? 'Piano attuale' : 'Attiva Premium',
      highlighted: true,
      color: 'amber',
      disabled: currentTier === 'pro'
    }
  ];

  // Premium benefits details
  const premiumBenefits = [
    {
      icon: MessageSquare,
      title: 'Risposte illimitate',
      description: 'Rispondi a tutte le richieste personalizzate senza limiti settimanali'
    },
    {
      icon: Globe,
      title: 'Social e Sito Web',
      description: 'Aggiungi i tuoi link social e il sito web nella vetrina per più visibilità'
    },
    {
      icon: Clock,
      title: 'Priorità richieste',
      description: 'Vedi le nuove richieste 24 ore prima degli utenti Free'
    },
    {
      icon: TrendingUp,
      title: 'Boost visibilità',
      description: 'I tuoi prodotti appaiono più in alto nei risultati di ricerca'
    },
    {
      icon: BarChart3,
      title: 'Analytics avanzate',
      description: 'Statistiche dettagliate su visite, conversioni e performance'
    },
    {
      icon: Sparkles,
      title: 'Badge Creator Plus',
      description: 'Badge esclusivo che aumenta la fiducia dei clienti'
    },
    {
      icon: Eye,
      title: 'Ranking migliorato',
      description: 'Leggero boost nel posizionamento nei risultati di ricerca'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Hero */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Badge className="mb-4 bg-[#008080]/10 text-[#008080] hover:bg-[#008080]/20">
              Diventa Artigiano
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Scegli il tuo percorso
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Inizia gratuitamente con tutte le funzionalità essenziali, 
              o potenzia il tuo profilo con Premium.
            </p>
          </motion.div>
        </div>

        <LaunchPricingNotice className="max-w-3xl mx-auto mb-12 -mt-6" />

        {/* Tipi di Crafter - Solo per non-crafter */}
        {!isCrafterEnabled && (
          <div className="mb-20">
            <h2 className="text-2xl font-bold text-center mb-2">Tipologia Venditore</h2>
            <p className="text-slate-600 text-center mb-8">Scegli la tipologia più adatta a te</p>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {crafterTypes.map((type, idx) => {
                const IconComponent = type.icon;
                return (
                  <motion.div
                    key={type.id}
                    initial={{ opacity: 0, x: idx === 0 ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <Card className={`p-6 h-full border-2 hover:shadow-lg transition-all cursor-pointer ${
                      type.color === 'emerald' ? 'hover:border-emerald-400' : 'hover:border-red-400'
                    }`}>
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${
                          type.color === 'emerald' ? 'bg-emerald-100' : 'bg-red-100'
                        }`}>
                          <IconComponent className={`w-6 h-6 ${
                            type.color === 'emerald' ? 'text-emerald-600' : 'text-red-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-bold">{type.name}</h3>
                            <span className="text-lg">{type.badge.split(' ')[0]}</span>
                          </div>
                          <p className="text-slate-600 mb-4">{type.description}</p>
                          <ul className="space-y-2 mb-4">
                            {type.requirements.map((req, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                                <Check className={`w-4 h-4 ${
                                  type.color === 'emerald' ? 'text-emerald-500' : 'text-red-500'
                                }`} />
                                {req}
                              </li>
                            ))}
                          </ul>
                          <Button
                            onClick={() => handleSelectPlan('free', type.id)}
                            className={`w-full ${
                              type.color === 'emerald' 
                                ? 'bg-emerald-500 hover:bg-emerald-600' 
                                : 'bg-red-500 hover:bg-red-600'
                            }`}
                          >
                            Scegli {type.name}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Piani */}
        <div className="mb-20">
          <h2 className="text-2xl font-bold text-center mb-2">
            {isCrafterEnabled ? 'Potenzia il tuo profilo' : 'Piani e Prezzi'}
          </h2>
          <p className="text-slate-600 text-center mb-8">
            Stesse commissioni (8%) per tutti. La differenza è nei vantaggi!
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, idx) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className={`p-6 h-full relative ${
                  plan.highlighted 
                    ? 'border-2 border-amber-400 shadow-lg shadow-amber-100'
                    : 'border border-slate-200'
                }`}>
                  {plan.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Consigliato
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold mt-2">{plan.name}</h3>
                    <div className="mt-2">
                      {plan.id === 'premium' && launchMode ? (
                        <>
                          <span className="text-4xl font-bold">Incluso</span>
                          <span className="text-slate-500 block text-sm mt-1">
                            gratis durante il lancio · poi {plan.price}{plan.period}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl font-bold">{plan.price}</span>
                          <span className="text-slate-500">{plan.period}</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-2">{plan.description}</p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        {feature.included ? (
                          <Check className={`w-4 h-4 flex-shrink-0 ${feature.highlight ? 'text-amber-500' : 'text-emerald-500'}`} />
                        ) : (
                          <X className="w-4 h-4 text-slate-300 flex-shrink-0" />
                        )}
                        <span className={`${feature.included ? 'text-slate-700' : 'text-slate-400'} ${feature.highlight ? 'font-medium' : ''}`}>
                          {feature.text}
                        </span>
                        {feature.highlight && (
                          <Zap className="w-3 h-3 text-amber-500" />
                        )}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`w-full ${
                      plan.highlighted
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                    disabled={plan.disabled || (plan.id === 'premium' && launchMode)}
                    onClick={() => {
                      if (plan.id === 'free' && !isCrafterEnabled) {
                        setShowActivationDialog(true);
                      } else if (plan.id === 'premium') {
                        handleSelectPlan('premium', profile?.crafter_type || 'hobbista');
                      }
                    }}
                  >
                    {plan.id === 'premium' && launchMode ? 'Incluso durante il lancio' : plan.cta}
                    {!plan.disabled && !(plan.id === 'premium' && launchMode) && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Premium Benefits Detail */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Cosa ottieni con Premium</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {premiumBenefits.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                >
                  <Card className="p-5 h-full hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-amber-100">
                        <IconComponent className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-1">{benefit.title}</h3>
                        <p className="text-sm text-slate-600">{benefit.description}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Fee Transparency */}
        <div className="mb-16 max-w-2xl mx-auto">
          <Card className="p-6 bg-[#008080]/5 border-[#008080]/20">
            <div className="text-center">
              <h3 className="font-bold text-lg text-[#008080] mb-2">Commissioni trasparenti</h3>
              <p className="text-slate-600 mb-4">
                Tutti pagano la stessa commissione dell&apos;8% sulle vendite (minimo €1 sugli
                ordini piccoli), indipendentemente dal piano. La differenza è solo nei
                vantaggi di visibilità e produttività!
              </p>
              <div className="flex justify-center gap-6 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#008080]">8%</div>
                  <div className="text-slate-500">su ogni vendita</div>
                  <div className="text-slate-500">Commissioni sulla gestione del pagamento incluse</div>
                </div>

              </div>
            </div>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-[#008080] to-[#006666] rounded-2xl p-12 text-white">
          <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-3xl font-bold mb-4">Pronto a iniziare?</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Unisciti alla community di artigiani italiani e inizia a vendere le tue creazioni uniche.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            {!isCrafterEnabled ? (
              <Button 
                size="lg"
                className="bg-white text-[#008080] hover:bg-slate-100"
                onClick={() => navigate(user ? '/plans' : '/signup')}
              >
                {user ? 'Diventa Artigiano' : 'Registrati Gratis'}
              </Button>
            ) : (
              <Button 
                size="lg"
                className="bg-white text-[#008080] hover:bg-slate-100"
                onClick={() => navigate('/dashboard')}
              >
                Vai alla Dashboard
              </Button>
            )}
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/crafters')}
            >
              Scopri gli Artigiani
            </Button>
          </div>
        </div>

        {/* Activation Dialog */}
        <Dialog open={showActivationDialog} onOpenChange={setShowActivationDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {selectedType === 'hobbista' ? '🟢 Diventa Hobbista' : '🔴 Diventa Artigiano Professionista'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {selectedType === 'professionista' && (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-1">Partita IVA Richiesta</p>
                        <p>Per diventare Artigiano Professionista devi avere una P.IVA italiana valida.</p>
                        <p className="mt-2 font-medium">Questa scelta è irreversibile: non potrai tornare al profilo Hobbista.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="vat">Partita IVA</Label>
                    <Input
                      id="vat"
                      value={vatNumber}
                      onChange={(e) => {
                        const v = e.target.value;
                        setVatNumber(v);
                        if (v.trim()) {
                          const res = validatePIVA(v);
                          setVatError(res.valid ? '' : (res.error || 'P.IVA non valida'));
                        } else {
                          setVatError('');
                        }
                      }}
                      placeholder="IT12345678901"
                      className={`mt-2 ${vatError ? 'border-red-500' : ''}`}
                      data-testid="activation-piva-input"
                    />
                    {vatError && (
                      <p className="text-xs text-red-500 mt-1">{vatError}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">La P.IVA sarà verificata</p>
                  </div>
                </div>
              )}

              {selectedType === 'hobbista' && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex gap-3">
                    <Heart className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-800">
                      <p className="font-medium mb-1">Perfetto per iniziare!</p>
                      <p>Come Hobbista puoi vendere le tue creazioni senza bisogno di Partita IVA.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Con il piano Free avrai:</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    Vetrina completa
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    Prodotti illimitati
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    5 risposte a richieste/settimana
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    8% commissione sulle vendite
                  </li>
                </ul>
              </div>

              <Button
                onClick={handleActivateCrafter}
                disabled={activating || (selectedType === 'professionista' && (!vatNumber.trim() || !!vatError))}
                data-testid="activate-crafter-submit-btn"
                className={`w-full py-6 ${
                  selectedType === 'hobbista' 
                    ? 'bg-emerald-500 hover:bg-emerald-600' 
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {activating ? (
                  'Attivazione in corso...'
                ) : (
                  <>
                    Attiva il mio profilo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                Potrai sempre passare a Premium dalla tua dashboard
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
